import { afterNextRender, Directive, inject, Injector, ViewChild } from '@angular/core';
import { UnsavedChangesDialogComponent } from './unsaved-changes-dialog.component';

/** A page that can be asked before it is left (unsavedChangesGuard). */
export interface ConfirmsLeave {
  confirmLeave(): boolean | Promise<boolean>;
}

/**
 * Unsaved Changes for a form page. The page puts `<app-unsaved-changes-dialog>` in its template and returns its field
 * values from `formState()`. The form's first render is the clean state; an Edit page calls `markCleanAfterRender()`
 * once its record loads, `markClean()` after a save that stays on the page, and `markSaved()` after one that leaves.
 */
@Directive()
export abstract class UnsavedChangesForm implements ConfirmsLeave {
  @ViewChild(UnsavedChangesDialogComponent) protected unsavedChangesDialog?: UnsavedChangesDialogComponent;

  private readonly injector = inject(Injector);
  private cleanState: string | null = null;
  private saved = false;

  constructor() {
    // A baseline even if the page never calls markClean(), so it can't silently never ask.
    afterNextRender(() => {
      if (this.cleanState === null) {
        this.markClean();
      }
    });
  }

  /** The values the user can change, in any serialisable shape. */
  protected abstract formState(): unknown;

  protected markClean(): void {
    this.cleanState = JSON.stringify(this.formState());
    this.saved = false;
  }

  /**
   * The values just set (an Edit page's loaded record) become clean once the fields have rendered with them, so a field's
   * first emission (the auth form's, for one) doesn't count as an edit.
   */
  protected markCleanAfterRender(): void {
    afterNextRender(() => this.markClean(), { injector: this.injector });
  }

  protected markSaved(): void {
    this.saved = true;
  }

  hasUnsavedChanges(): boolean {
    if (this.saved || this.cleanState === null) {
      return false;
    }
    return JSON.stringify(this.formState()) !== this.cleanState;
  }

  /** True to leave now; otherwise asks, and resolves with the answer. */
  confirmLeave(): boolean | Promise<boolean> {
    if (!this.hasUnsavedChanges() || !this.unsavedChangesDialog) {
      return true;
    }
    return this.unsavedChangesDialog.ask();
  }
}
